import { useState } from 'react';
import { Button } from '@/ui/Button';
import { Card, CardHeader } from '@/ui/Card';
import { Field, Input, Select } from '@/ui/Field';
import { SkeletonFilas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { useGuardarMarca, useMarca, useSubirImagenDeMarca, type Marca } from './api';

/**
 * Personalización — logo, nombres, colores, firma y desconexión laboral.
 *
 * ## Lo que cambia
 *
 * - **El archivo elegido ya no vive en `window`.** El vanilla lo guardaba en
 *   `window._brandLogoFile` y `window._brandSigFile` y montaba los dos
 *   selectores con un `setTimeout(…, 50)` porque el contenedor todavía no estaba
 *   en el DOM. Si el guardado fallaba a mitad, el archivo se quedaba colgado en
 *   el objeto global hasta recargar.
 * - **Se ve lo que se va a subir antes de subirlo.** Solo se veía la imagen que
 *   YA estaba guardada, así que elegir un logo y no ver ningún cambio era lo
 *   normal.
 * - **La lista de días hábiles se lee.** Eran siete casillas con la abreviatura
 *   al lado y sin agrupar: «casilla Lun, casilla Mar…» sin decir de qué.
 * - **El campo de dominio avisa de lo que rompe.** Se usa en los enlaces de los
 *   correos y de los certificados; el aviso estaba, pero encima del campo y en
 *   un bloque que se lee como decoración.
 */
export function Personalizacion() {
  const consulta = useMarca(true);
  const guardar = useGuardarMarca();
  const subir = useSubirImagenDeMarca();

  const b = consulta.data;
  const [listo, setListo] = useState(false);
  const [f, setF] = useState<Marca>({});
  const [logo, setLogo] = useState<File | null>(null);
  const [firma, setFirma] = useState<File | null>(null);

  if (!listo && b) { setF(b); setListo(true); }

  if (consulta.isPending) return <SkeletonFilas filas={8} columnas={2} />;
  if (consulta.isError) {
    return (
      <EstadoError
        err={consulta.error}
        que="la personalización del portal"
        onReintentar={() => void consulta.refetch()}
      />
    );
  }

  const dias = (f.work_days ?? '1,2,3,4,5').split(',').filter(Boolean);

  function cambiar<K extends keyof Marca>(campo: K, valor: Marca[K]) {
    setF((previo) => ({ ...previo, [campo]: valor }));
  }

  async function enviar() {
    try {
      // Las imágenes primero: son subidas independientes y el resto del
      // formulario no debe quedar a medias si una falla.
      if (logo) { await subir.mutateAsync({ cual: 'logo', archivo: logo }); setLogo(null); }
      if (firma) { await subir.mutateAsync({ cual: 'signature', archivo: firma }); setFirma(null); }
      await guardar.mutateAsync({
        portal_name: f.portal_name ?? '',
        company_name: f.company_name ?? '',
        nombre_institucional: f.nombre_institucional ?? '',
        login_subtitle: f.login_subtitle ?? '',
        primary_color: f.primary_color ?? '#0D2B4E',
        accent_color: f.accent_color ?? '#F5A623',
        show_landing: Boolean(f.show_landing),
        signature_name: f.signature_name ?? '',
        app_url: f.app_url ?? '',
        disconnect_enabled: Boolean(f.disconnect_enabled),
        work_start_hour: f.work_start_hour ?? 8,
        work_end_hour: f.work_end_hour ?? 18,
        work_days: dias.join(',') || '1,2,3,4,5',
      });
      Utils.toast('Personalización guardada. Recarga para ver todos los cambios.', 'success');
    } catch (e) {
      Utils.toast((e as Error).message, 'error');
    }
  }

  const trabajando = guardar.isPending || subir.isPending;

  return (
    <Card className="max-w-2xl">
      <CardHeader
        titulo={<><i className="bi bi-brush" aria-hidden="true" /> Personalización del portal</>}
      />
      <form
        className="flex flex-col gap-4 p-4"
        onSubmit={(e) => { e.preventDefault(); void enviar(); }}
      >
        <ImagenDeMarca
          etiqueta="Logo del portal"
          medida="230 × 70 px"
          ayuda="PNG o JPG. Se muestra sobre fondo blanco."
          acepta="image/*"
          urlActual={f.logo_url}
          archivo={logo}
          onArchivo={setLogo}
          ancho="w-[230px] h-[70px]"
          respaldo={<span className="text-7 font-black text-brand-ink">B</span>}
        />

        <Field label="Nombre del portal" opcional>
          <Input
            value={f.portal_name ?? ''}
            onChange={(e) => cambiar('portal_name', e.target.value)}
          />
        </Field>
        <Field label="Nombre de la empresa" opcional>
          <Input
            value={f.company_name ?? ''}
            onChange={(e) => cambiar('company_name', e.target.value)}
          />
        </Field>
        <Field
          label="Firma de las publicaciones institucionales"
          opcional
          ayuda="El nombre con el que salen las publicaciones marcadas como OFICIAL en Novedades."
        >
          <Input
            maxLength={120}
            value={f.nombre_institucional ?? ''}
            onChange={(e) => cambiar('nombre_institucional', e.target.value)}
            placeholder="Bienestar Su Servicio"
          />
        </Field>
        <Field label="Subtítulo de la pantalla de entrada" opcional>
          <Input
            value={f.login_subtitle ?? ''}
            onChange={(e) => cambiar('login_subtitle', e.target.value)}
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Color primario">
            <Input
              type="color"
              className="h-10 w-20 p-1"
              value={f.primary_color ?? '#0D2B4E'}
              onChange={(e) => cambiar('primary_color', e.target.value)}
            />
          </Field>
          <Field label="Color de acento">
            <Input
              type="color"
              className="h-10 w-20 p-1"
              value={f.accent_color ?? '#F5A623'}
              onChange={(e) => cambiar('accent_color', e.target.value)}
            />
          </Field>
        </div>

        <label className="flex items-start gap-2 text-3">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={Boolean(f.show_landing)}
            onChange={(e) => cambiar('show_landing', e.target.checked)}
          />
          <span>Mostrar el recorrido de bienvenida a los usuarios nuevos</span>
        </label>

        <hr className="border-line" />

        <h3 className="text-3 font-bold text-brand-ink">
          <i className="bi bi-pen" aria-hidden="true" /> Firma para los certificados
        </h3>
        <ImagenDeMarca
          etiqueta="Imagen de la firma"
          medida="200 × 80 px"
          ayuda="PNG sin fondo (transparente). Aparece sobre la línea de firma del certificado."
          acepta="image/png"
          urlActual={f.signature_url}
          archivo={firma}
          onArchivo={setFirma}
          ancho="w-[200px] h-[80px]"
          respaldo={<span className="text-2 text-muted">Sin firma</span>}
        />
        <Field label="Nombre bajo la firma" opcional>
          <Input
            value={f.signature_name ?? ''}
            onChange={(e) => cambiar('signature_name', e.target.value)}
            placeholder="Coordinador de Bienestar"
          />
        </Field>

        <hr className="border-line" />

        <Field
          label="Dominio del portal (APP_URL)"
          opcional
          ayuda={`Debe empezar por http:// o https://. Si se deja en blanco se usa el del entorno (${f.env_app_url || 'no definido'}). Se usa en los enlaces de los correos y de los certificados: un valor equivocado los rompe todos.`}
        >
          <Input
            value={f.app_url ?? ''}
            onChange={(e) => cambiar('app_url', e.target.value)}
            placeholder={f.env_app_url || 'https://intranet.gruposuservicio.com'}
          />
        </Field>

        <hr className="border-line" />

        <h3 className="text-3 font-bold text-brand-ink">
          <i className="bi bi-moon-stars" aria-hidden="true" /> Desconexión laboral (Ley 2191/2022)
        </h3>
        <label className="flex items-start gap-2 text-3">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={Boolean(f.disconnect_enabled)}
            onChange={(e) => cambiar('disconnect_enabled', e.target.checked)}
          />
          <span>
            No enviar notificaciones fuera del horario laboral. Las que se intenten enviar fuera
            de hora se <strong>difieren automáticamente</strong> al siguiente horario hábil.
          </span>
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Hora de inicio">
            <Select
              value={String(f.work_start_hour ?? 8)}
              onChange={(e) => cambiar('work_start_hour', Number(e.target.value))}
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
              ))}
            </Select>
          </Field>
          <Field label="Hora de fin">
            <Select
              value={String(f.work_end_hour ?? 18)}
              onChange={(e) => cambiar('work_end_hour', Number(e.target.value))}
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
              ))}
            </Select>
          </Field>
        </div>

        <fieldset>
          <legend className="mb-1 text-2 font-semibold text-ink">Días hábiles</legend>
          <div className="flex flex-wrap gap-3">
            {([['1', 'Lunes'], ['2', 'Martes'], ['3', 'Miércoles'], ['4', 'Jueves'],
              ['5', 'Viernes'], ['6', 'Sábado'], ['7', 'Domingo']] as const).map(([d, nombre]) => (
              <label key={d} className="flex items-center gap-1.5 text-3">
                <input
                  type="checkbox"
                  checked={dias.includes(d)}
                  onChange={(e) => {
                    const otros = dias.filter((x) => x !== d);
                    cambiar('work_days',
                      (e.target.checked ? [...otros, d] : otros).sort().join(','));
                  }}
                />
                {nombre}
              </label>
            ))}
          </div>
        </fieldset>

        <p className="pt-1">
          <Button type="submit" loading={trabajando}>
            <i className="bi bi-floppy" aria-hidden="true" /> Guardar
          </Button>
        </p>
      </form>
    </Card>
  );
}

/**
 * Un logo o una firma, con lo que hay guardado y lo que se acaba de elegir.
 *
 * La vista previa de lo elegido sale de `URL.createObjectURL` y se revoca al
 * cambiar de archivo: sin eso, cada intento deja un blob en memoria.
 */
function ImagenDeMarca({
  etiqueta, medida, ayuda, acepta, urlActual, archivo, onArchivo, ancho, respaldo,
}: {
  etiqueta: string;
  medida: string;
  ayuda: string;
  acepta: string;
  urlActual?: string | null;
  archivo: File | null;
  onArchivo: (f: File | null) => void;
  ancho: string;
  respaldo: React.ReactNode;
}) {
  const [previa, setPrevia] = useState<string | null>(null);

  function elegir(nuevo: File | null) {
    setPrevia((anterior) => {
      if (anterior) URL.revokeObjectURL(anterior);
      return nuevo ? URL.createObjectURL(nuevo) : null;
    });
    onArchivo(nuevo);
  }

  const mostrada = previa ?? urlActual;

  return (
    <div className="flex flex-wrap items-start gap-4">
      <span
        className={`flex shrink-0 items-center justify-center overflow-hidden rounded-sm border border-line bg-white ${ancho}`}
      >
        {mostrada ? (
          <img src={mostrada} alt={previa ? `${etiqueta}: la imagen elegida` : `${etiqueta} actual`}
            className="max-h-full max-w-full object-contain" />
        ) : respaldo}
      </span>
      <div className="min-w-48 flex-1">
        <Field
          label={`${etiqueta} (${medida})`}
          opcional
          ayuda={archivo ? `Se subirá «${archivo.name}» al guardar.` : ayuda}
        >
          <Input
            type="file"
            accept={acepta}
            onChange={(e) => elegir(e.target.files?.[0] ?? null)}
          />
        </Field>
        {archivo && (
          <p className="mt-1">
            <Button variant="ghost" size="sm" onClick={() => elegir(null)}>
              <i className="bi bi-x-lg" aria-hidden="true" /> Descartar la imagen elegida
            </Button>
          </p>
        )}
      </div>
    </div>
  );
}
